import { formatDate } from "@/lib/utils";

export type SortOrder = "asc" | "desc";

/** Customers list column key → API `order[...]` field. */
export const CUSTOMER_SORT_FIELDS: Record<string, string> = {
  name: "customer.fullName",
  phone: "customer.phone",
  firstOrderAt: "firstOrderAt",
  lastOrderAt: "lastOrderAt",
  totalOrders: "totalOrders",
  totalSpent: "totalSpent",
};

/** The customers list opens on the latest visit first. */
export const DEFAULT_CUSTOMER_SORT: { column: string; order: SortOrder } = {
  column: "lastOrderAt",
  order: "desc",
};

interface CustomerListParamsInput {
  page: number;
  limit: number;
  search?: string;
  sortColumn?: string;
  sortOrder?: SortOrder;
}

/**
 * Query params for GET /restaurants/{id}/customers. The sort is always sent
 * to the API so it applies across pages, never sorted client-side.
 */
export function buildCustomerListParams({
  page,
  limit,
  search,
  sortColumn = DEFAULT_CUSTOMER_SORT.column,
  sortOrder = DEFAULT_CUSTOMER_SORT.order,
}: CustomerListParamsInput): Record<string, string | number> {
  const params: Record<string, string | number> = { page, limit };

  if (search) {
    const isPhoneSearch = /^[\d+]+$/.test(search);
    params[isPhoneSearch ? "customer.phone" : "customer.fullName"] = search;
  }

  const sortField = CUSTOMER_SORT_FIELDS[sortColumn] ?? sortColumn;
  params[`order[${sortField}]`] = sortOrder;

  return params;
}

/**
 * « Client depuis le … » line of the customer page. `firstOrderAt` is null
 * when the customer has no order at this restaurant yet; `new Date(null)`
 * would show 01/01/1970 (SFRESTO-138).
 */
export const formatCustomerSince = (firstOrderAt: string | null): string =>
  firstOrderAt
    ? `Client depuis le ${formatDate(new Date(firstOrderAt), false)}`
    : "Client depuis le —";
